import {
  stepsWithPdfTemplates,
  type WorkflowFieldType,
  type WorkflowStep,
  type WorkflowTemplate,
} from '@/api/workflows';

/**
 * One ShortText/Number field plus enough breadcrumb info to label it inside the
 * E-Signature drawer's field Selects (so the originator can tell apart "First
 * name" inside Step 2 / Personal Info from "First name" inside Step 4 / Joint
 * Holder Info). Steps + sections are skipped if their bilingual label is empty
 * — the FE never persists an empty label, so this guards legacy seed data.
 */
export type FieldChoice = {
  id: string;
  stepId: string;
  stepLabel: string;
  sectionLabel: string;
  fieldLabel: string;
  type: WorkflowFieldType;
};

const NAME_TYPES: ReadonlyArray<WorkflowFieldType> = ['ShortText'];
const EMAIL_TYPES: ReadonlyArray<WorkflowFieldType> = ['ShortText'];
const PHONE_TYPES: ReadonlyArray<WorkflowFieldType> = ['ShortText', 'Number'];

const localized = (en: string, fr: string, isFr: boolean) => (isFr ? fr : en) || en || fr;

/**
 * Walk every DynamicForm step and collect candidate fields by allowed field
 * type. Today every signer slot accepts ShortText except `phone`, which also
 * accepts Number (BMO's payload schema says `phone: number`). The filter is
 * deliberately loose — we trust the originator to pick a sensible field rather
 * than locking down by `validate` rule.
 */
export function collectFieldChoices(
  template: WorkflowTemplate,
  isFr: boolean,
  allowedTypes: ReadonlyArray<WorkflowFieldType>,
): FieldChoice[] {
  const out: FieldChoice[] = [];
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    const stepLabel = localized(step.titleEn, step.titleFr, isFr);
    for (const section of step.sections) {
      const sectionLabel = localized(section.titleEn, section.titleFr, isFr);
      for (const row of section.rows) {
        for (const column of row.columns) {
          const f = column.field;
          if (!f) continue;
          if (!allowedTypes.includes(f.type)) continue;
          out.push({
            id: f.id,
            stepId: step.id,
            stepLabel,
            sectionLabel,
            fieldLabel: localized(f.labelEn, f.labelFr, isFr),
            type: f.type,
          });
        }
      }
    }
  }
  return out;
}

export const NAME_FIELD_TYPES = NAME_TYPES;
export const EMAIL_FIELD_TYPES = EMAIL_TYPES;
export const PHONE_FIELD_TYPES = PHONE_TYPES;

export type PdfEntryChoice = {
  id: string;
  stepId: string;
  stepLabel: string;
  label: string;
};

/** Flatten every PDF source template into a single picker list. */
export function collectPdfEntries(template: WorkflowTemplate, isFr: boolean): PdfEntryChoice[] {
  const out: PdfEntryChoice[] = [];
  for (const step of stepsWithPdfTemplates(template)) {
    const stepLabel = localized(step.titleEn, step.titleFr, isFr);
    for (const e of step.fileEntries) {
      out.push({ id: e.id, stepId: step.id, stepLabel, label: e.label || stepLabel });
    }
  }
  return out;
}

/** Build a stable display label for one field choice. */
export function describeField(choice: FieldChoice): string {
  return `${choice.stepLabel} › ${choice.sectionLabel} › ${choice.fieldLabel}`;
}

/**
 * Walk a step's `oneSpanConfig` and return a quick `{signers, placements}`
 * count so the originator sees configuration depth on the step card without
 * opening the drawer. Used by `WorkflowSteps` for the chip next to the title.
 */
export function summariseOneSpanConfig(step: WorkflowStep): {
  signers: number;
  placements: number;
} {
  const cfg = step.oneSpanConfig;
  if (!cfg) return { signers: 0, placements: 0 };
  const placements = cfg.documents.reduce((sum, d) => sum + d.fields.length, 0);
  return { signers: cfg.roles.length, placements };
}
